import { Icon } from '@iconify/react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { DayPicker } from 'react-day-picker/buddhist'
import type { DateRange } from 'react-day-picker'
import { Card } from '@/components/ui/Card'
import { Input, SearchInput } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { PROJECT_STATUS_LABELS, PROVINCE_NAMES, PROCUREMENT_METHOD_LABELS } from '@/lib/constants'
import { cn } from '@/lib/utils'
import 'react-day-picker/style.css'

interface TenderFiltersProps {
  onFiltersChange: (filters: FilterValues) => void
  className?: string
}

export interface FilterValues {
  search?: string
  status?: string[]
  province?: string[]
  procurementMethod?: string[]
  minBudget?: string
  maxBudget?: string
  dateFrom?: string
  dateTo?: string
}

type FilterFormValues = FilterValues

const ALLOWED_METHOD_FILTERS = ['e-market', 'e-bidding', 'selective'] as const
const ALLOWED_METHOD_FILTER_SET = new Set<string>(ALLOWED_METHOD_FILTERS)

const STATUS_OPTIONS = Object.entries(PROJECT_STATUS_LABELS).map(([value, labels]) => ({
  value,
  label: labels.simple,
}))

const METHOD_OPTIONS = ALLOWED_METHOD_FILTERS.map((value) => ({
  value,
  label: PROCUREMENT_METHOD_LABELS[value],
}))

const METHOD_OPTION_LABELS = METHOD_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label
  return acc
}, {})

const METHOD_LABELS: Record<string, string> = METHOD_OPTION_LABELS

const PROVINCE_OPTIONS = Object.entries(PROVINCE_NAMES).map(([value, label]) => ({
  value,
  label,
}))

const BUDGET_PRESET_VALUES = ['100000', '1000000', '10000000', '100000000'] as const
const BUDGET_PRESET_SET = new Set<string>(BUDGET_PRESET_VALUES)

function parseDateFromFilterValue(value?: string): Date | undefined {
  if (!value) return undefined
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return undefined
  return new Date(year, month - 1, day)
}

function formatDateForFilterValue(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDateRangeLabel(range?: DateRange): string {
  if (!range?.from && !range?.to) return 'เลือกช่วงวันที่'

  const formatter = new Intl.DateTimeFormat('th-TH', {
    dateStyle: 'medium',
  })

  if (range.from && range.to) {
    return `${formatter.format(range.from)} - ${formatter.format(range.to)}`
  }

  if (range.from) {
    return `เริ่ม ${formatter.format(range.from)}`
  }

  return `สิ้นสุด ${formatter.format(range.to!)}`
}

function formatProvinceSelectionLabel(provinces: string[]): string {
  if (provinces.length === 0) return 'ทุกจังหวัด'

  const labels = provinces.map((province) => PROVINCE_NAMES[province] || province)
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]}, ${labels[1]}`
  return `${labels[0]} +${labels.length - 1}`
}

function sanitizeBudgetValue(value: string): string {
  return value.replace(/\D/g, '')
}

function formatBudgetValue(value: string): string {
  const sanitized = sanitizeBudgetValue(value)
  if (!sanitized) return ''
  return Number(sanitized).toLocaleString('en-US')
}

function formatBudgetSelectionLabel(value: string, placeholder: string): string {
  if (!value) return placeholder
  return `${formatBudgetValue(value)} บาท`
}

interface BudgetDropdownProps {
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
}

function BudgetDropdown({ label, placeholder, value, onChange }: BudgetDropdownProps) {
  const customValue = value && !BUDGET_PRESET_SET.has(value) ? value : ''
  const selectedLabel = value ? `${formatBudgetValue(value)} บาท` : 'ยังไม่ได้เลือก'

  return (
    <div className="min-w-0">
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
      </label>
      <details className="relative group">
        <summary className="list-none w-full">
          <div className="h-10 px-3 rounded-lg border border-slate-200 bg-white flex items-center gap-2 text-sm cursor-pointer hover:border-slate-300 transition-colors">
            <Icon icon="solar:wallet-money-line-duotone" className="w-4 h-4 text-slate-500" />
            <span
              className={cn(
                'flex-1 min-w-0 truncate text-sm',
                value ? 'text-slate-700' : 'text-slate-500',
              )}
            >
              {formatBudgetSelectionLabel(value, placeholder)}
            </span>
            <Icon
              icon="solar:alt-arrow-down-line-duotone"
              className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180"
            />
          </div>
        </summary>

        <div className="absolute left-0 top-[calc(100%+0.5rem)] z-30 w-full min-w-[18rem] rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
          <div className="grid grid-cols-2 gap-2">
            {BUDGET_PRESET_VALUES.map((preset) => {
              const isSelected = value === preset

              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onChange(preset)}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-sm font-medium text-left transition-colors',
                    isSelected
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
                  )}
                >
                  {formatBudgetValue(preset)}
                </button>
              )
            })}
          </div>

          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="mb-2 text-xs font-medium text-slate-600">กำหนดเอง</p>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="ระบุจำนวนเงิน"
              value={customValue}
              onChange={(event) => onChange(sanitizeBudgetValue(event.target.value))}
            />
          </div>

          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
            <p className="text-xs text-slate-500">{selectedLabel}</p>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                <Icon icon="solar:trash-bin-trash-line-duotone" className="w-3.5 h-3.5" />
                ล้าง
              </button>
            )}
          </div>
        </div>
      </details>
    </div>
  )
}

export function TenderFilters({ onFiltersChange, className }: TenderFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams()

  const { register, watch, setValue, reset, handleSubmit } = useForm<FilterFormValues>({
    defaultValues: {
      search: searchParams.get('q') || '',
      status: searchParams.getAll('status'),
      province:
        searchParams.getAll('province').length > 0
          ? searchParams.getAll('province')
          : searchParams.get('province')
            ? [searchParams.get('province') as string]
            : [],
      procurementMethod: searchParams
        .getAll('method')
        .filter((method) => ALLOWED_METHOD_FILTER_SET.has(method)),
      minBudget: searchParams.get('minBudget') || '',
      maxBudget: searchParams.get('maxBudget') || '',
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || '',
    },
  })

  const watchedStatus = watch('status') || []
  const watchedProvinces = watch('province') || []
  const watchedMethod = watch('procurementMethod') || []
  const watchedDateFrom = watch('dateFrom')
  const watchedDateTo = watch('dateTo')
  const watchedMinBudget = watch('minBudget') || ''
  const watchedMaxBudget = watch('maxBudget') || ''
  const selectedDateRange: DateRange | undefined = (() => {
    const from = parseDateFromFilterValue(watchedDateFrom)
    const to = parseDateFromFilterValue(watchedDateTo)
    if (!from && !to) return undefined
    return { from, to }
  })()

  const toggleStatus = (status: string) => {
    const current = watchedStatus
    const updated = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status]
    setValue('status', updated)
  }

  const toggleMethod = (method: string) => {
    const current = watchedMethod
    const updated = current.includes(method)
      ? current.filter((m) => m !== method)
      : [...current, method]
    setValue('procurementMethod', updated)
  }

  const toggleProvince = (province: string) => {
    const current = watchedProvinces
    const updated = current.includes(province)
      ? current.filter((value) => value !== province)
      : [...current, province]
    setValue('province', updated)
  }

  const setBudgetValue = (field: 'minBudget' | 'maxBudget', value: string) => {
    setValue(field, sanitizeBudgetValue(value), {
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  const onSubmit = (data: FilterFormValues) => {
    // Update URL params
    const params = new URLSearchParams()
    if (data.search) params.set('q', data.search)
    data.province?.forEach((province) => params.append('province', province))
    if (data.minBudget) params.set('minBudget', data.minBudget)
    if (data.maxBudget) params.set('maxBudget', data.maxBudget)
    if (data.dateFrom) params.set('dateFrom', data.dateFrom)
    if (data.dateTo) params.set('dateTo', data.dateTo)
    data.status?.forEach((s) => params.append('status', s))
    data.procurementMethod?.forEach((m) => params.append('method', m))

    setSearchParams(params)
    onFiltersChange(data)
  }

  const clearFilters = () => {
    reset({
      search: '',
      status: [],
      province: [],
      procurementMethod: [],
      minBudget: '',
      maxBudget: '',
      dateFrom: '',
      dateTo: '',
    })
    setSearchParams(new URLSearchParams())
    onFiltersChange({})
  }

  const activeFilterCount = [
    watchedStatus.length > 0,
    watchedMethod.length > 0,
    watchedProvinces.length > 0,
    !!watchedMinBudget || !!watchedMaxBudget,
    !!watchedDateFrom || !!watchedDateTo,
  ].filter(Boolean).length

  return (
    <Card className={cn('p-4 font-display', className)}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-slate-900">ตัวกรอง</h3>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              ล้างทั้งหมด
            </button>
          )}
        </div>

        {/* Search */}
        <SearchInput
          placeholder="ค้นหาประกาศ..."
          {...register('search')}
        />
        <input type="hidden" {...register('minBudget')} />
        <input type="hidden" {...register('maxBudget')} />

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            สถานะ
          </label>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleStatus(opt.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors',
                  watchedStatus.includes(opt.value)
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Province + Date Range */}
        <div className="flex items-end gap-3">
          <div className="min-w-0 flex-[3]">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              จังหวัด
            </label>
            <details className="relative group">
              <summary className="list-none w-full">
                <div className="h-10 px-3 rounded-lg border border-slate-200 bg-white flex items-center gap-2 text-sm cursor-pointer hover:border-slate-300 transition-colors">
                  <Icon icon="solar:map-point-line-duotone" className="w-4 h-4 text-slate-500" />
                  <span
                    className={cn(
                      'flex-1 min-w-0 truncate text-sm',
                      watchedProvinces.length > 0 ? 'text-slate-700' : 'text-slate-500',
                    )}
                  >
                    {formatProvinceSelectionLabel(watchedProvinces)}
                  </span>
                  <Icon
                    icon="solar:alt-arrow-down-line-duotone"
                    className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180"
                  />
                </div>
              </summary>

              <div className="absolute left-0 top-[calc(100%+0.5rem)] z-30 w-full min-w-[18rem] max-h-[20rem] rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                <div className="max-h-[13rem] overflow-y-auto pr-1 space-y-1">
                  {PROVINCE_OPTIONS.map((opt) => {
                    const checked = watchedProvinces.includes(opt.value)
                    return (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleProvince(opt.value)}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{opt.label}</span>
                      </label>
                    )
                  })}
                </div>

                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                  <p className="text-xs text-slate-500">
                    เลือกแล้ว {watchedProvinces.length} จังหวัด
                  </p>
                  {watchedProvinces.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setValue('province', [])}
                      className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
                    >
                      <Icon icon="solar:trash-bin-trash-line-duotone" className="w-3.5 h-3.5" />
                      ล้างจังหวัด
                    </button>
                  )}
                </div>
              </div>
            </details>
          </div>

          <div className="min-w-0 flex-[2]">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              วันประกาศ
            </label>

            <details className="relative group">
              <summary className="list-none w-full">
                <div className="h-10 px-3 rounded-lg border border-slate-200 bg-white flex items-center gap-2 text-sm cursor-pointer hover:border-slate-300 transition-colors">
                  <Icon icon="solar:calendar-line-duotone" className="w-4 h-4 text-slate-500" />
                  <span
                    className={cn(
                      'flex-1 min-w-0 truncate text-sm',
                      selectedDateRange?.from ? 'text-slate-700' : 'text-slate-500',
                    )}
                  >
                    {selectedDateRange?.from ? formatDateRangeLabel(selectedDateRange) : 'เลือกช่วงวันที่'}
                  </span>
                  <Icon
                    icon="solar:alt-arrow-down-line-duotone"
                    className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180"
                  />
                </div>
              </summary>

              <div className="absolute right-0 top-[calc(100%+0.5rem)] z-30 w-fit max-w-[calc(100vw-3rem)] rounded-xl border border-slate-200 bg-white p-3 shadow-xl overflow-x-auto">
                <DayPicker
                  className="pq-date-range-picker"
                  mode="range"
                  numerals="latn"
                  pagedNavigation
                  numberOfMonths={2}
                  selected={selectedDateRange}
                  onSelect={(range) => {
                    setValue('dateFrom', range?.from ? formatDateForFilterValue(range.from) : '')
                    setValue('dateTo', range?.to ? formatDateForFilterValue(range.to) : '')
                  }}
                />

                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <p className="text-xs text-slate-500">{formatDateRangeLabel(selectedDateRange)}</p>
                  {(selectedDateRange?.from || selectedDateRange?.to) && (
                    <button
                      type="button"
                      onClick={() => {
                        setValue('dateFrom', '')
                        setValue('dateTo', '')
                      }}
                      className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
                    >
                      <Icon icon="solar:trash-bin-trash-line-duotone" className="w-3.5 h-3.5" />
                      ล้างวันที่
                    </button>
                  )}
                </div>
              </div>
            </details>
          </div>
        </div>

        {/* Procurement Method */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            วิธีการจัดซื้อจัดจ้าง
          </label>

          <div className="flex flex-wrap gap-2">
            {METHOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleMethod(opt.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors',
                  watchedMethod.includes(opt.value)
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Budget Range */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            ช่วงงบประมาณ (บาท)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <BudgetDropdown
              label="งบขั้นต่ำ"
              placeholder="ต่ำสุด"
              value={watchedMinBudget}
              onChange={(value) => setBudgetValue('minBudget', value)}
            />
            <BudgetDropdown
              label="งบสูงสุด"
              placeholder="สูงสุด"
              value={watchedMaxBudget}
              onChange={(value) => setBudgetValue('maxBudget', value)}
            />
          </div>
        </div>

        {/* Submit */}
        <Button type="submit" className="w-full">
          <Icon icon="solar:magnifer-line-duotone" className="w-4 h-4 mr-2" />
          ใช้ตัวกรอง
        </Button>
      </form>
    </Card>
  )
}

// Compact filter bar for mobile/inline use
export function TenderFilterBar({
  onFiltersChange,
  className,
}: TenderFiltersProps) {
  const [searchParams] = useSearchParams()
  const activeFilters = [
    ...searchParams.getAll('status'),
    ...searchParams.getAll('province'),
    ...searchParams.getAll('method'),
  ].filter((value): value is string => Boolean(value))
  const resolveFilterLabel = (filter: string) =>
    PROJECT_STATUS_LABELS[filter as keyof typeof PROJECT_STATUS_LABELS]?.simple ||
    METHOD_LABELS[filter] ||
    PROVINCE_NAMES[filter] ||
    filter

  return (
    <div className={cn('flex items-center gap-2 overflow-x-auto pb-2', className)}>
      <SearchInput
        placeholder="ค้นหา..."
        className="w-48 flex-shrink-0"
        onChange={(e) => onFiltersChange({ search: e.target.value })}
        defaultValue={searchParams.get('q') || ''}
      />

      {activeFilters.map((filter) => (
        <Badge key={filter} variant="secondary" className="flex-shrink-0">
          {resolveFilterLabel(filter)}
          <button className="ml-1 hover:text-slate-700">
            <Icon icon="solar:close-circle-line-duotone" className="w-3.5 h-3.5" />
          </button>
        </Badge>
      ))}
    </div>
  )
}
